'use client';
import React, { useState } from 'react';

import {
  Elements,
  PaymentMethodMessagingElement,
} from '@stripe/react-stripe-js';

import styles from './styles.module.css';

import { Spinner } from '@/components/Spinner';
import { type PaymentProvidersResponse } from '@/modules/checkout/aliases';
import { type LocationsEnabled } from '@/modules/location/constants.ts';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation.ts';
import {
  getStripeCountryCode,
  getStripeCurrencyCode,
} from '@/modules/stripe/helpers.ts';
import { useLoadStripe } from '@/modules/stripe/useLoadStripe.ts';

type Props = {
  data: PaymentProvidersResponse;
  productPrice: number;
};

export function StripePaymentMethodMessaging({ data, productPrice }: Props) {
  const { stripe, hasErrored } = useLoadStripe(data.stripe.publishable_key);
  const { location } = useCurrentLocation();

  const [isReady, setIsReady] = useState(false);

  if (hasErrored) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Elements
        stripe={stripe}
        options={{
          fonts: [
            {
              src: `url('${process.env.NEXT_PUBLIC_DEPOP_CLOUDFRONT_HOST}/web/assets/fonts/AktivGroteskEx_W_Basic_Rg.woff2')`,
              family: 'Aktiv Grotesk',
              weight: '400',
            },
          ],
          appearance: {
            variables: {
              fontSizeBase: '15px',
              fontSizeSm: '0.75rem',
              // this is copied straight from the web-thrift-ui black since we can't access theme colours here
              colorText: '#262626',
              fontFamily: 'Aktiv Grotesk',
            },
          },
        }}
      >
        {!isReady && <Spinner className={styles.spinner} />}
        <PaymentMethodMessagingElement
          onReady={() => setIsReady(true)}
          options={{
            amount: Math.round(Number(productPrice) * 100),
            currency: getStripeCurrencyCode(location as keyof LocationsEnabled),
            paymentMethodTypes: ['klarna'],
            countryCode: getStripeCountryCode(
              location as keyof LocationsEnabled
            ),
          }}
        />
      </Elements>
    </div>
  );
}
